import { useState, useEffect } from 'react';
import axios from 'axios';

const ContactForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    comment: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    return () => setIsVisible(false);
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.comment.trim()) newErrors.comment = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await axios.post(`${apiUrl}/api/contact`, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', comment: '' });
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTouchMove = (e) => {
    e.stopPropagation();
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/70 p-5 backdrop-blur-sm transition-all duration-300 ${isVisible ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
      onClick={handleOverlayClick}
      onTouchMove={handleTouchMove}
    >
      <div 
        className={`relative my-5 w-full max-w-[500px] max-h-[90vh] overflow-y-auto rounded-2xl bg-gradient-to-br from-white to-[#f5f7fa] p-6 shadow-[0_20px_40px_rgba(0,0,0,0.15),0_5px_15px_rgba(0,0,0,0.1)] transition-all duration-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top gradient bar */}
        <div className="absolute top-0 left-0 w-full h-2 rounded-t-2xl bg-gradient-to-r from-[#4f46e5] via-[#10b981] to-[#3b82f6]" />
        
        <button 
          className="absolute top-2 right-2 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-transparent text-3xl text-[#6b7280] transition-all duration-300 hover:text-[#4f46e5] hover:scale-110 active:scale-90"
          onClick={handleCloseClick}
          aria-label="Close contact form"
        >×</button>
        
        <h2 className="mt-4 mb-6 text-center text-2xl font-semibold bg-gradient-to-r from-[#4f46e5] to-[#3b82f6] bg-clip-text text-transparent sm:text-[1.8rem]">Get in Touch</h2>

        {submitStatus === 'success' ? (
          <div className="py-8 text-center text-[#10b981]">
            <svg viewBox="0 0 24 24" className="mx-auto mb-4 h-12 w-12 fill-current">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
            <h3 className="mb-2 text-2xl font-semibold">Thank you!</h3>
            <p className="text-base text-[#6b7280]">Your message has been sent successfully.</p>
          </div>
        ) : submitStatus === 'error' ? (
          <div className="py-8 text-center text-[#ef4444]">
            <svg viewBox="0 0 24 24" className="mx-auto mb-4 h-12 w-12 fill-current">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <h3 className="mb-2 text-2xl font-semibold">Oops!</h3>
            <p className="text-base text-[#6b7280]">Something went wrong. Please try again later.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={`peer w-full border-b-2 border-[#e5e7eb] bg-transparent px-2 pb-2 pt-4 text-base text-[#1f2937] outline-none transition-all duration-300 focus:border-[#4f46e5] ${errors.name ? 'border-[#ef4444]' : ''}`}
                placeholder=" "
              />
              <label htmlFor="name" className="pointer-events-none absolute left-2 top-4 text-base text-[#6b7280] transition-all duration-300 peer-focus:-translate-y-6 peer-focus:scale-[0.85] peer-focus:text-[#4f46e5] peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:scale-[0.85]">Name *</label>
              {errors.name && <span className="mt-1 block text-sm text-[#ef4444]">{errors.name}</span>}
            </div>
            
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`peer w-full border-b-2 border-[#e5e7eb] bg-transparent px-2 pb-2 pt-4 text-base text-[#1f2937] outline-none transition-all duration-300 focus:border-[#4f46e5] ${errors.email ? 'border-[#ef4444]' : ''}`}
                placeholder=" "
              />
              <label htmlFor="email" className="pointer-events-none absolute left-2 top-4 text-base text-[#6b7280] transition-all duration-300 peer-focus:-translate-y-6 peer-focus:scale-[0.85] peer-focus:text-[#4f46e5] peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:scale-[0.85]">Email *</label>
              {errors.email && <span className="mt-1 block text-sm text-[#ef4444]">{errors.email}</span>}
            </div>

            <div className="relative">
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className={`peer w-full border-b-2 border-[#e5e7eb] bg-transparent px-2 pb-2 pt-4 text-base text-[#1f2937] outline-none transition-all duration-300 focus:border-[#4f46e5] ${errors.phone ? 'border-[#ef4444]' : ''}`}
                placeholder=" "
              />
              <label htmlFor="phone" className="pointer-events-none absolute left-2 top-4 text-base text-[#6b7280] transition-all duration-300 peer-focus:-translate-y-6 peer-focus:scale-[0.85] peer-focus:text-[#4f46e5] peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:scale-[0.85]">Mobile Number *</label>
              {errors.phone && <span className="mt-1 block text-sm text-[#ef4444]">{errors.phone}</span>}
            </div>
            
            <div className="relative">
              <textarea
                id="comment"
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                required
                className={`peer w-full resize-none border-b-2 border-[#e5e7eb] bg-transparent px-2 pb-2 pt-4 text-base text-[#1f2937] outline-none transition-all duration-300 focus:border-[#4f46e5] ${errors.comment ? 'border-[#ef4444]' : ''}`}
                rows="4"
                placeholder=" "
              />
              <label htmlFor="comment" className="pointer-events-none absolute left-2 top-4 text-base text-[#6b7280] transition-all duration-300 peer-focus:-translate-y-6 peer-focus:scale-[0.85] peer-focus:text-[#4f46e5] peer-[:not(:placeholder-shown)]:-translate-y-6 peer-[:not(:placeholder-shown)]:scale-[0.85]">Message *</label>
              {errors.comment && <span className="mt-1 block text-sm text-[#ef4444]">{errors.comment}</span>}
            </div>
            
            <button 
              type="submit" 
              className="mt-4 w-full rounded-lg bg-gradient-to-r from-[#4f46e5] to-[#3b82f6] px-4 py-3 text-base font-medium text-white shadow-[0_4px_6px_rgba(79,70,229,0.2)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_6px_12px_rgba(79,70,229,0.3)] active:translate-y-0 disabled:cursor-not-allowed disabled:bg-[#e5e7eb] disabled:shadow-none disabled:transform-none"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Sending...
                </span>
              ) : (
                'Send Message'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactForm;